import { isNil } from "lodash";
import { useUser } from "@/context/UserContext";

interface GAEventData {
    [key: string]: any;
}

interface AdditionalInfo {
    isAuthenticated?: boolean;
    referralCode?: string;
    userArea?: string;
    finalScore?: number;
    userScore?: number;
    practiceFinalScore?: number;
    userRank?: number;
    mlsId?: string;
    buttonLabel?: string;
    numberOfGuesses?: number;
    challengeId?: string;
    challengeStatus?: string;
    challengeGuestRegisterModalVersion?: string;
    userLocation?: string;
    guestFinalScoreModalVersion?: string;
    guestChallengeRedirect?: string;
    challengeRegisterButton?: string;
    plusMinusGuess?: number;
    plusMinusCorrect?: boolean;
    plusMinusVersion?: string;
    liveVariantVersion?: string;
    propertyMode?: string;
    visitorStatus?: string;
    newVersion?: string;
    community?: string;
    archiveDate?: string;
    leaderboardType?: string;
}

interface GA {
    (command: string, eventName: string, data: GAEventData): void;
}

declare global {
    interface Window {
        gtag?: GA;
    }
}

const useGAEvent = () => {
    const { userState } = useUser();
    const referral_code = localStorage.getItem("referral_code");
    const eventData: GAEventData = {};

    /**
     * Sends an event to Google Analytics with additional information.
     *
     * This function constructs the event data based on the provided information
     * such as user authentication status, referral code, user area, and final score.
     *
     * @param eventName - The name of the event to be sent to Google Analytics.
     * @param additionalInfo - Additional information for constructing the event data.
     * @returns void
     *
     * @example
     * // Example usage:
     * sendEvent("button_click", {
     *    isAuthenticated: true,
     *    referralCode: "ABC123",
     *    userArea: "Chicago",
     *    finalScore: 150,
     *    userScore: 100,
     *    practiceFinalScore: 80,
     *    userRank: 5,
     *    mlsId: "MLS123456",
     *    buttonLabel: "Keep Playing",
     *    numberOfGuesses: 3,
     *    challengeId: "CH123456",
     *    challengeStatus: "completed",
     *    userLocation: "New York"
     * });
     */
    const sendEvent = (eventName: string, additionalInfo: AdditionalInfo): void => {
        const {
            isAuthenticated,
            referralCode,
            userArea,
            finalScore,
            userScore,
            practiceFinalScore,
            userRank,
            mlsId,
            buttonLabel,
            numberOfGuesses,
            challengeId,
            challengeStatus,
            challengeGuestRegisterModalVersion,
            userLocation,
            guestFinalScoreModalVersion,
            guestChallengeRedirect,
            challengeRegisterButton,
            plusMinusGuess,
            plusMinusCorrect,
            plusMinusVersion,
            liveVariantVersion,
            propertyMode,
            visitorStatus,
            newVersion,
            community,
            archiveDate,
            leaderboardType,
        } = additionalInfo;

        if (window) {
            if (!isNil(isAuthenticated)) {
                let userStatus: string | null = null;

                if (isAuthenticated) {
                    userStatus = "registered";
                } else {
                    if (!isNil(referral_code) || !isNil(referralCode)) {
                        userStatus = "guest_referral";
                    } else {
                        userStatus = "guest";
                    }
                }

                eventData["user_status"] = userStatus;
            }

            if (!isNil(userArea)) {
                eventData["user_area"] = userArea;
            }

            if (!isNil(finalScore)) {
                eventData["user_final_score"] = finalScore ?? 0;
            }

            if (!isNil(practiceFinalScore)) {
                eventData["practice_final_score"] = practiceFinalScore ?? 0;
            }

            if (!isNil(userScore)) {
                eventData["user_score"] = userScore ?? 0;
            }

            if (!isNil(userRank)) {
                eventData["user_rank"] = userState?.showScoreLeaderboard ? (userRank ?? 0) : "Hidden";
            }

            if (!isNil(userLocation)) {
                eventData["user_location"] = userLocation;
            }

            if (!isNil(mlsId)) {
                eventData["mls_id"] = mlsId;
            }

            if (!isNil(buttonLabel)) {
                eventData["button_label"] = buttonLabel;
            }

            if (!isNil(numberOfGuesses)) {
                eventData["num_guesses"] = numberOfGuesses ?? 0;
            }

            if (!isNil(challengeId)) {
                eventData["challenge_id"] = challengeId ?? 0;
            }

            if (!isNil(challengeGuestRegisterModalVersion)) {
                eventData["challenge_guest_register_modal_version"] = challengeGuestRegisterModalVersion;
            }

            if (!isNil(challengeStatus)) {
                eventData["challenge_status"] = challengeStatus;
            }

            if (!isNil(guestFinalScoreModalVersion)) {
                eventData["guest_final_score_modal_version"] = guestFinalScoreModalVersion;
            }

            if (!isNil(guestChallengeRedirect)) {
                eventData["guest_challenge_redirect"] = guestChallengeRedirect;
            }

            if (!isNil(challengeRegisterButton)) {
                eventData["challenge_register_button"] = challengeRegisterButton;
            }

            if (!isNil(plusMinusGuess)) {
                eventData["guess"] = plusMinusGuess;
            }

            if (!isNil(propertyMode)) {
                eventData["property_mode"] = propertyMode;
            }

            if (!isNil(plusMinusCorrect)) {
                eventData["correct"] = plusMinusCorrect;
            }

            if (!isNil(liveVariantVersion) || !isNil(plusMinusVersion)) {
                eventData["version"] = liveVariantVersion || plusMinusVersion;
            }

            if (!isNil(newVersion)) {
                eventData["new_version"] = newVersion;
            }

            if (!isNil(visitorStatus)) {
                eventData["visitor_status"] = visitorStatus;
            }

            if (!isNil(community)) {
                eventData["community"] = community;
            }

            if (!isNil(archiveDate)) {
                eventData["archive_date"] = archiveDate;
            }

            if (!isNil(leaderboardType)) {
                eventData["leaderboard_type"] = leaderboardType;
            }

            // Send the event to Google Analytics
            if (window.gtag) {
                window.gtag("event", eventName, eventData);
            }
        }
    };

    return { sendEvent };
};

export default useGAEvent;
